import { Button, Switch, notify } from '@affine/component';
import {
  SettingRow,
  SettingWrapper,
} from '@affine/component/setting-components';
import { WorkspaceServerService } from '@affine/core/modules/cloud';
import { WorkspacePermissionService } from '@affine/core/modules/permissions';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { UserFriendlyError } from '@affine/error';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import {
  type UpdateWorkspaceDiscoveryResponse,
  updateWorkspaceDiscoveryMutation,
  type WorkspaceDiscoveryGql,
  type WorkspaceDiscoverySettingResponse,
  workspaceDiscoverySettingQuery,
} from './discovery-operations';

type LoadState = 'idle' | 'loading' | 'error';

export const WorkspaceDiscoveryPanel = () => {
  const t = useI18n();
  const workspace = useService(WorkspaceService).workspace;
  const workspaceServer = useService(WorkspaceServerService);
  const workspacePermission = useService(WorkspacePermissionService).permission;
  const server = useLiveData(workspaceServer.server$);
  const isOwnerOrAdmin = useLiveData(workspacePermission.isOwnerOrAdmin$);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('loading');
  const [isSaving, setIsSaving] = useState(false);

  const canManageDiscovery =
    workspace.flavour !== 'local' &&
    !workspace.openOptions.isSharedMode &&
    isOwnerOrAdmin === true;

  useEffect(() => {
    workspacePermission.revalidate();
  }, [workspacePermission]);

  const load = useCallback(async () => {
    if (!server) {
      setEnabled(null);
      setLoadState('error');
      return;
    }

    setLoadState('loading');
    try {
      const gql = server.gql as unknown as WorkspaceDiscoveryGql;
      const data = await gql<WorkspaceDiscoverySettingResponse>({
        query: workspaceDiscoverySettingQuery,
        variables: { workspaceId: workspace.id },
      });
      setEnabled(data.workspaceDiscoverySetting.enabled);
      setLoadState('idle');
    } catch (error) {
      setEnabled(null);
      setLoadState('error');
      notify.error(UserFriendlyError.fromAny(error));
    }
  }, [server, workspace.id]);

  useEffect(() => {
    if (!canManageDiscovery) {
      return;
    }
    void load();
  }, [canManageDiscovery, load]);

  const update = useCallback(
    async (nextEnabled: boolean) => {
      if (!server || enabled === null || isSaving) {
        return;
      }

      setIsSaving(true);
      try {
        const gql = server.gql as unknown as WorkspaceDiscoveryGql;
        const data = await gql<UpdateWorkspaceDiscoveryResponse>({
          query: updateWorkspaceDiscoveryMutation,
          variables: {
            input: {
              workspaceId: workspace.id,
              enabled: nextEnabled,
            },
          },
        });
        setEnabled(data.updateWorkspaceDiscovery.enabled);
        setLoadState('idle');
      } catch (error) {
        notify.error(UserFriendlyError.fromAny(error));
        await load();
      } finally {
        setIsSaving(false);
      }
    },
    [enabled, isSaving, load, server, workspace.id]
  );

  if (!canManageDiscovery) {
    return null;
  }

  return (
    <SettingWrapper
      title={t['com.affine.settings.workspace.discovery.title']()}
    >
      <SettingRow
        name={t['com.affine.settings.workspace.discovery.allow.title']()}
        desc={t['com.affine.settings.workspace.discovery.allow.description']()}
      >
        {loadState === 'error' ? (
          <Button
            variant="secondary"
            disabled={isSaving}
            onClick={() => void load()}
            data-testid="workspace-discovery-retry"
          >
            {t['com.affine.settings.workspace.discovery.retry']()}
          </Button>
        ) : (
          <Switch
            checked={enabled ?? false}
            disabled={loadState !== 'idle' || isSaving}
            onChange={(checked: boolean) => void update(checked)}
            data-testid="workspace-discovery-toggle"
          />
        )}
      </SettingRow>
    </SettingWrapper>
  );
};
